import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, ExternalLink } from 'lucide-react';
import { useRestaurant } from '../../context/RestaurantContext';
import { locationsService } from '../../services/locationsService';
import apiClient from '../../services/api';
import { Link, useParams } from 'react-router-dom';

/** Open Buffet Insights: one report per route, all from the insights endpoint with branch and window filters. */

// report key → [title, blurb]. Order = the tab strip + sidebar order.
const REPORTS = [
  ['overview', 'Overview', 'Covers, revenue and cost per cover, wastage — the buffet health check at a glance.'],
  ['covers', 'Weekly Covers', 'Lunch vs dinner covers week by week — spot growth, dips and seasonality.'],
  ['food-cost', 'Food Cost vs Target', 'Food cost as % of revenue against your target line, week by week.'],
  ['wastage', 'Wastage Heatmap', 'Which dish gets wasted on which weekday — % of planned prep thrown away.'],
  ['uptake', 'Uptake Accuracy', 'Assumed vs actual dish uptake — where the prep model over- or under-cooks.'],
  ['economics', 'Dish Economics', 'Per-dish consumption, waste and cost per cover, ranked by share of waste Rs.'],
  ['bookings-buffet', 'Bookings by Buffet', 'Bookings taken against the people who actually came, per buffet — with no-shows and how much of the room had booked.'],
];

// Chart palette — validated (dataviz): blue+green pass CVD/contrast checks.
const C_BLUE = '#2a78d6';
const C_GREEN = '#008300';
const C_GRID = '#e1e0d9';
const C_MUTED = '#898781';

// Mauritian rupees — "Rs 12,780". Never ₹ (the Indian rupee); prices are MUR.
const rupees = (n) => `Rs ${Math.round(n).toLocaleString('en-MU')}`;
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const heatColor = (v) => {
  if (v == null) return { bg: '#f4f4f2', fg: '#b9b8b2' };
  if (v < 5) return { bg: '#d9f2e4', fg: '#1d6b45' };
  if (v < 15) return { bg: '#fdeeda', fg: '#8a5a1d' };
  if (v < 25) return { bg: '#f5c26b', fg: '#5f3c05' };
  return { bg: '#f0989a', fg: '#6d1113' };
};

const Kpi = ({ label, value, sub, subTone }) => (
  <div>
    <div className="text-sm text-slate-500">{label}</div>
    <div className="text-3xl font-bold text-slate-900 mt-1">{value}</div>
    {sub && (
      <div className={`text-xs mt-1 ${
        subTone === 'bad' ? 'text-red-600' : subTone === 'good' ? 'text-blue-600' : 'text-slate-400'
      }`}>{sub}</div>
    )}
  </div>
);

const Legend = ({ items }) => (
  <div className="flex items-center gap-4 text-xs text-slate-500 mb-2">
    {items.map(([color, label, dashed]) => (
      <span key={label} className="inline-flex items-center gap-1.5">
        {dashed
          ? <svg width="14" height="6"><line x1="0" y1="3" x2="14" y2="3" stroke={color} strokeWidth="2" strokeDasharray="3 2" /></svg>
          : <span className="h-2.5 w-2.5 rounded-sm" style={{ background: color }} />}
        {label}
      </span>
    ))}
  </div>
);

/** Multi-series line chart with y-grid, hover titles. series: [{key,color,dashed}] */
function LineChart({ rows, series, yFmt = (v) => v, height = 220 }) {
  const W = 760; const H = height; const padL = 44; const padR = 12; const padT = 10; const padB = 26;
  const vals = rows.flatMap((r) => series.map((s) => r[s.key]).filter((v) => v != null));
  if (!vals.length) return <p className="text-sm text-slate-400 py-6">No data in this window yet.</p>;
  let lo = Math.min(...vals); let hi = Math.max(...vals);
  if (lo === hi) { lo -= 1; hi += 1; }
  const span = hi - lo; lo -= span * 0.1; hi += span * 0.1;
  const x = (i) => padL + (i / Math.max(1, rows.length - 1)) * (W - padL - padR);
  const y = (v) => padT + (1 - (v - lo) / (hi - lo)) * (H - padT - padB);
  const ticks = Array.from({ length: 5 }, (_, i) => lo + ((hi - lo) * i) / 4);
  const path = (key) => rows
    .map((r, i) => (r[key] == null ? null : `${i === 0 || rows[i - 1][key] == null ? 'M' : 'L'}${x(i)},${y(r[key])}`))
    .filter(Boolean).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img">
      {ticks.map((t) => (
        <g key={t}>
          <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)} stroke={C_GRID} strokeWidth="1" />
          <text x={padL - 6} y={y(t) + 3} textAnchor="end" fontSize="10" fill={C_MUTED}>{yFmt(t)}</text>
        </g>
      ))}
      {rows.map((r, i) => (
        <text key={r.label} x={x(i)} y={H - 8} textAnchor="middle" fontSize="10" fill={C_MUTED}>{r.label}</text>
      ))}
      {series.map((s) => (
        <g key={s.key}>
          <path d={path(s.key)} fill="none" stroke={s.color} strokeWidth="2"
            strokeDasharray={s.dashed ? '5 4' : undefined} strokeLinejoin="round" />
          {rows.map((r, i) => r[s.key] != null && (
            <circle key={i} cx={x(i)} cy={y(r[s.key])} r="6" fill="transparent">
              <title>{`${r.label} ${s.name || s.key}: ${yFmt(r[s.key])}`}</title>
            </circle>
          ))}
        </g>
      ))}
    </svg>
  );
}

const selectCls = 'rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-orange-600';

export default function BuffetInsights() {
  const { restaurantId } = useRestaurant();
  // Which report this route shows (default: overview KPIs).
  const { report: reportParam } = useParams();
  const report = REPORTS.some(([k]) => k === reportParam) ? reportParam : 'overview';
  const [, reportTitle, reportBlurb] = REPORTS.find(([k]) => k === report);
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [buffets, setBuffets] = useState([]);
  const [buffetId, setBuffetId] = useState('');
  const [weeks, setWeeks] = useState(12);
  const [hmWeeks, setHmWeeks] = useState(4);
  const [target, setTarget] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!restaurantId) return;
    locationsService.list(restaurantId)
      .then((locs) => { setLocations(locs); setLocationId((c) => c || String(locs[0]?.id || '')); })
      .catch(() => setLocations([]));
  }, [restaurantId]);

  useEffect(() => {
    if (!restaurantId || !locationId) return;
    apiClient.get(`/users/restaurant/${restaurantId}/buffets/`, { params: { location_id: locationId } })
      .then(({ data: d }) => setBuffets((d?.success && d.results) || []))
      .catch(() => setBuffets([]));
  }, [restaurantId, locationId]);

  useEffect(() => {
    if (!restaurantId || !locationId) return;
    setLoading(true);
    const params = { location_id: locationId, weeks, heatmap_weeks: hmWeeks };
    if (buffetId) params.buffet_id = buffetId;
    apiClient.get(`/users/restaurant/${restaurantId}/buffets/insights/`, { params })
      .then(({ data: d }) => {
        if (!d?.success) throw new Error(d?.error || 'Failed to load insights');
        setData(d.data);
        setError('');
      })
      .catch((e) => setError(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [restaurantId, locationId, buffetId, weeks, hmWeeks]);

  const k = data?.kpis || {};
  const weekly = useMemo(() => data?.weekly || [], [data]);
  const foodCostRows = useMemo(
    () => weekly.map((w) => ({ label: w.label, actual: w.food_cost_pct, target })),
    [weekly, target],
  );

  return (
    <div className="p-6 w-full">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <div>
          <h1 className="font-display text-2xl font-semibold">{reportTitle}</h1>
        </div>
        <Link to="/reports/buffet" className="inline-flex items-center gap-1 text-sm text-orange-700 hover:text-orange-900">
          Classic buffet report <ExternalLink size={13} />
        </Link>
      </div>
      <p className="text-sm text-slate-500 mb-4">{reportBlurb}</p>

      {/* No report strip here: each report is its own sidebar entry, and a
          second row of the same links on the page said everything twice. */}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <select className={selectCls} value={locationId} onChange={(e) => { setLocationId(e.target.value); setBuffetId(''); }}>
          {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <select className={selectCls} value={buffetId} onChange={(e) => setBuffetId(e.target.value)}>
          <option value="">All buffets</option>
          {buffets.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <select className={selectCls} value={weeks} onChange={(e) => setWeeks(Number(e.target.value))}>
          {[4, 8, 12, 24].map((w) => <option key={w} value={w}>Trend · {w} weeks</option>)}
        </select>
        {report === 'wastage' && (
          <select className={selectCls} value={hmWeeks} onChange={(e) => setHmWeeks(Number(e.target.value))}>
            {[2, 4, 8, 12].map((w) => <option key={w} value={w}>Heatmap · {w} weeks</option>)}
          </select>
        )}
        {report === 'food-cost' && (
          <label className="inline-flex items-center gap-1.5 text-sm text-slate-600">
            Target
            <input type="number" min="5" max="60" value={target}
              onChange={(e) => setTarget(Number(e.target.value) || 30)}
              className="w-16 rounded-lg border border-slate-200 px-2 py-2 text-sm" />%
          </label>
        )}
      </div>

      {error && <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">{error}</div>}
      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 py-16 justify-center">
          <Loader2 size={18} className="animate-spin" /> Loading insights…
        </div>
      ) : data && (
        <>
          {/* KPI tiles */}
          {report === 'overview' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Kpi label={`Covers · ${data.weeks} weeks`} value={k.covers?.toLocaleString('en-MU') ?? '—'}
              sub={k.covers_delta_pct != null ? `${k.covers_delta_pct > 0 ? '+' : ''}${k.covers_delta_pct}% vs prior ${data.weeks}` : null}
              subTone={k.covers_delta_pct >= 0 ? 'good' : 'bad'} />
            <Kpi label="Revenue per cover" value={k.revenue_per_cover != null ? rupees(k.revenue_per_cover) : '—'}
              sub={k.revenue_delta_pct != null ? `revenue ${k.revenue_delta_pct > 0 ? '+' : ''}${k.revenue_delta_pct}%` : null}
              subTone={k.revenue_delta_pct >= 0 ? 'good' : 'bad'} />
            <Kpi label="Food cost per cover" value={k.food_cost_per_cover ? rupees(k.food_cost_per_cover) : '—'}
              sub={k.food_cost_per_cover ? 'from buffet studio estimates' : 'set food cost / cover in the studio'} />
            <Kpi label="Wastage cost / week" value={k.waste_cost_per_week ? rupees(k.waste_cost_per_week) : '—'}
              sub={k.waste_pct_of_food_spend != null ? `${k.waste_pct_of_food_spend}% of food spend` : null}
              subTone={k.waste_pct_of_food_spend > 5 ? 'bad' : undefined} />
          </div>
          )}

          {/* Weekly covers lunch vs dinner */}
          {report === 'covers' && (<>
          <h2 className="font-semibold text-slate-900 mb-1">Weekly covers · lunch vs dinner · {data.weeks} weeks</h2>
          <Legend items={[[C_BLUE, 'Lunch'], [C_GREEN, 'Dinner', true]]} />
          <LineChart rows={weekly} height={230}
            series={[
              { key: 'lunch', name: 'lunch', color: C_BLUE },
              { key: 'dinner', name: 'dinner', color: C_GREEN, dashed: true },
            ]} />
          </>)}

          {/* Food cost % vs target */}
          {report === 'food-cost' && (<>
          <h2 className="font-semibold text-slate-900 mb-1">Food cost % of revenue · vs {target}% target</h2>
          <Legend items={[[C_BLUE, 'Actual'], [C_MUTED, `Target ${target}%`, true]]} />
          <LineChart rows={foodCostRows} height={210} yFmt={(v) => `${Math.round(v)}%`}
            series={[
              { key: 'actual', name: 'actual', color: C_BLUE },
              { key: 'target', name: 'target', color: C_MUTED, dashed: true },
            ]} />
          </>)}

          {/* Wastage heatmap */}
          {report === 'wastage' && (<>
          <h2 className="font-semibold text-slate-900 mb-3">
            Wastage % heatmap · dish × weekday · last {data.heatmap_weeks} weeks avg
          </h2>
          {data.heatmap.length === 0 ? (
            <p className="text-sm text-slate-400">No kg waste logged here — log it from the Ops panel.</p>
          ) : (
            <>
              <table className="w-full text-sm border-separate" style={{ borderSpacing: '3px 3px' }}>
                <thead>
                  <tr>
                    <th className="text-left font-medium text-slate-500 pr-2" />
                    {WEEKDAYS.map((d) => <th key={d} className="font-medium text-slate-500 text-center">{d}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.heatmap.map((row) => (
                    <tr key={row.dish}>
                      <td className="text-slate-700 pr-2 whitespace-nowrap">{row.dish}</td>
                      {row.pct.map((v, i) => {
                        const c = heatColor(v);
                        return (
                          <td key={i} className="text-center rounded-md py-1.5 font-semibold"
                            style={{ background: c.bg, color: c.fg }}
                            title={`${row.dish} · ${WEEKDAYS[i]}: ${v == null ? 'no prep plan' : `${v}% of planned prep wasted`}`}>
                            {v == null ? '—' : Math.round(v)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                {[['<5%', heatColor(2)], ['5–15%', heatColor(10)], ['15–25%', heatColor(20)], ['>25%', heatColor(30)]].map(([l, c]) => (
                  <span key={l} className="inline-flex items-center gap-1.5">
                    <span className="h-3 w-3 rounded-sm" style={{ background: c.bg }} /> {l}
                  </span>
                ))}
              </div>
            </>
          )}
          </>)}

          {/* Assumed vs actual uptake */}
          {report === 'uptake' && (<>
          <h2 className="font-semibold text-slate-900 mb-1">Assumed vs actual uptake · where the prep model is wrong</h2>
          <Legend items={[[C_MUTED, 'Assumed'], [C_BLUE, `Actual (${data.weeks * 7}-day est.)`]]} />
          {data.uptake.length === 0 ? (
            <p className="text-sm text-slate-400">Add a dish plan in the studio to compare.</p>
          ) : (
            <div className="space-y-3">
              {data.uptake.slice(0, 8).map((u) => (
                <div key={u.dish} className="flex items-center gap-3">
                  <span className="w-36 shrink-0 truncate text-xs text-slate-600 text-right">{u.dish}</span>
                  <div className="flex-1 space-y-1">
                    <div className="h-3.5 rounded-r bg-slate-100">
                      <div className="h-3.5 rounded-r" style={{ width: `${Math.min(100, u.assumed || 0)}%`, background: C_MUTED }}
                        title={`Assumed ${u.assumed}%`} />
                    </div>
                    <div className="h-3.5 rounded-r bg-slate-100">
                      <div className="h-3.5 rounded-r" style={{ width: `${Math.min(100, u.actual || 0)}%`, background: C_BLUE }}
                        title={`Actual ${u.actual == null ? '—' : `${u.actual}%`}`} />
                    </div>
                  </div>
                  <span className="w-24 shrink-0 text-xs text-slate-500 tabular-nums">
                    {u.assumed ?? '—'}% → {u.actual ?? '—'}%
                  </span>
                </div>
              ))}
              <p className="text-xs text-slate-400">
                Actual is planned prep minus logged waste. Re-fit the studio&apos;s uptake monthly.
              </p>
            </div>
          )}
          </>)}

          {/* Dish economics */}
          {report === 'economics' && (<>
          <h2 className="font-semibold text-slate-900 mb-3">Dish-level economics · last {data.weeks} weeks</h2>
          {data.economics.length === 0 ? (
            <p className="text-sm text-slate-400">Add a dish plan with cost per kg to see economics.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="py-2 font-medium">Dish</th>
                  <th className="py-2 font-medium text-right">Cost/kg</th>
                  <th className="py-2 font-medium text-right">Consumed</th>
                  <th className="py-2 font-medium text-right">Wasted</th>
                  <th className="py-2 font-medium text-right">Cost/cover</th>
                  <th className="py-2 font-medium pl-6">Share of waste Rs</th>
                </tr>
              </thead>
              <tbody>
                {data.economics.map((e) => (
                  <tr key={e.dish} className="border-b border-slate-100">
                    <td className="py-2 text-slate-800">{e.dish}</td>
                    <td className="py-2 text-right tabular-nums">{e.cost_per_kg != null ? rupees(e.cost_per_kg) : <span className="text-slate-300">set in studio</span>}</td>
                    <td className="py-2 text-right tabular-nums">{e.consumed_kg} kg</td>
                    <td className="py-2 text-right tabular-nums">{e.wasted_kg} kg</td>
                    <td className="py-2 text-right tabular-nums">{e.cost_per_cover != null ? rupees(e.cost_per_cover) : '—'}</td>
                    <td className="py-2 pl-6">
                      {e.waste_share_pct != null ? (
                        <div className="h-2.5 w-full max-w-[160px] rounded-full bg-slate-100">
                          <div className="h-2.5 rounded-full" title={`${e.waste_share_pct}% of waste cost (${rupees(e.waste_cost)})`}
                            style={{
                              width: `${Math.min(100, e.waste_share_pct)}%`,
                              background: e.waste_share_pct > 40 ? '#d03b3b' : e.waste_share_pct > 15 ? '#eda100' : '#0ca30c',
                            }} />
                        </div>
                      ) : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          </>)}

          {/* Bookings: the diary against the door, with Attended coming from the till's own cover sales. */}
          {report === 'bookings-buffet' && (() => {
            const rows = data.bookings_by_buffet || [];
            const live = rows.filter((r) => r.bookings > 0 || r.attended > 0);
            const tot = live.reduce((a, r) => ({
              bookings: a.bookings + r.bookings,
              booked_guests: a.booked_guests + r.booked_guests,
              attended: a.attended + r.attended,
              no_shows: a.no_shows + r.no_shows,
              cancelled: a.cancelled + r.cancelled,
            }), { bookings: 0, booked_guests: 0, attended: 0, no_shows: 0, cancelled: 0 });
            return (
              <>
                <h2 className="font-semibold text-slate-900 mb-1">
                  Bookings by buffet · last {data.weeks} weeks
                </h2>
                <p className="mb-3 text-xs text-slate-500">
                  <strong>Booked</strong> is the diary; <strong>Attended</strong> is everyone
                  through the door.
                </p>
                {live.length === 0 ? (
                  <p className="text-sm text-slate-400">
                    No buffet bookings or covers in this window yet.
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-500 border-b border-slate-200">
                        <th className="py-2 font-medium">Buffet</th>
                        <th className="py-2 font-medium text-right">Bookings</th>
                        <th className="py-2 font-medium text-right">Guests booked</th>
                        <th className="py-2 font-medium text-right">Attended</th>
                        <th className="py-2 font-medium text-right">No-shows</th>
                        <th className="py-2 font-medium text-right">Cancelled</th>
                        <th className="py-2 font-medium pl-6">Booked share of the room</th>
                      </tr>
                    </thead>
                    <tbody>
                      {live.map((r) => (
                        <tr key={r.buffet_id} className="border-b border-slate-100">
                          <td className="py-2 text-slate-800 whitespace-nowrap">{r.name}</td>
                          <td className="py-2 text-right tabular-nums">{r.bookings}</td>
                          <td className="py-2 text-right tabular-nums">
                            {r.booked_guests}
                            {r.kids_booked > 0 && (
                              <span className="text-slate-400"> · {r.kids_booked} kids</span>
                            )}
                          </td>
                          <td className="py-2 text-right tabular-nums font-medium text-slate-900">
                            {r.attended}
                          </td>
                          <td className={`py-2 text-right tabular-nums ${
                            r.no_shows > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                            {r.no_shows || '—'}
                          </td>
                          <td className="py-2 text-right tabular-nums text-slate-400">
                            {r.cancelled || '—'}
                          </td>
                          <td className="py-2 pl-6">
                            {r.booked_share_pct != null ? (
                              <span className="inline-flex items-center gap-2">
                                <span className="h-2.5 w-full max-w-[140px] rounded-full bg-slate-100">
                                  <span className="block h-2.5 rounded-full"
                                    title={`${r.booked_share_pct}% of the room had booked`}
                                    style={{
                                      width: `${Math.min(100, r.booked_share_pct)}%`,
                                      background: C_BLUE,
                                    }} />
                                </span>
                                <span className="tabular-nums text-xs text-slate-500">
                                  {r.booked_share_pct}%
                                </span>
                              </span>
                            ) : <span className="text-slate-300 text-xs">walk-ins only</span>}
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t-2 border-slate-200 font-semibold text-slate-900">
                        <td className="py-2">Total</td>
                        <td className="py-2 text-right tabular-nums">{tot.bookings}</td>
                        <td className="py-2 text-right tabular-nums">{tot.booked_guests}</td>
                        <td className="py-2 text-right tabular-nums">{tot.attended}</td>
                        <td className="py-2 text-right tabular-nums">{tot.no_shows || '—'}</td>
                        <td className="py-2 text-right tabular-nums">{tot.cancelled || '—'}</td>
                        <td className="py-2 pl-6" />
                      </tr>
                    </tbody>
                  </table>
                )}
              </>
            );
          })()}
        </>
      )}
    </div>
  );
}
